import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useStyletron} from "baseui";
import {Frame, NeedHelp} from "../z40-uicomp/frame";
import {Cell, Grid} from "baseui/layout-grid";
import logo from "../z30-images/logo_lyw_full_400x200.png";
import mobileHero from "../z30-images/wC3ptwSTkvc-unsplash.jpg"
import personalisedLearningScreenshot from "../z30-images/Personalised learning screenshot 2.png";
import simplePlanningScreenshot from "../z30-images/Simple planning screenshot.png";
import homeEducationValuesScreenshot from "../z30-images/Home education values screenshot 2.png";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../z70-shadcn/ui/card";
import {LinkButton} from "../z40-uicomp/buttons";

function FeatureScreenshot({ src, alt, rotateClass = "-rotate-2", large = false }) {
    const [expanded, setExpanded] = useState(false);

    const imgClass = large
        ? "h-28 sm:h-32 w-auto max-w-[11.5rem] sm:max-w-[13rem] object-contain object-top pointer-events-none"
        : "h-24 sm:h-28 w-auto max-w-[10rem] sm:max-w-[11.5rem] object-contain object-top pointer-events-none";

    return (
        <div className={`mt-4 flex justify-center ${expanded ? "relative z-20" : ""}`}>
            <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                aria-expanded={expanded}
                aria-label={expanded ? `${alt}, click to return to normal size` : `${alt}, click to enlarge`}
                title={expanded ? "Click to return to normal size" : "Click to enlarge"}
                className={`border border-black bg-white p-1 shadow-md cursor-pointer transition-all duration-200 ease-out origin-center ${
                    expanded ? "rotate-0 scale-[2] shadow-xl" : `${rotateClass} scale-100`
                }`}
            >
                <img
                    src={src}
                    alt={alt}
                    className={imgClass}
                />
            </button>
        </div>
    );
}

export function Home() {
    
            return (
                <Frame>
                    <div className="lyw-container min-h-screen bg-gradient-to-br from-white to-slate-50 pt-8 pb-8 px-6 sm:px-12 lg:px-24">
                        <div className="max-w-6xl mx-auto">
                            {/* Hero Section */}
                            <div className="grid gap-12 lg:grid-cols-2 items-center mb-16">
                                {/* Left Column - Content */}
                                <div className="space-y-8">
                                    <div className="space-y-6">
                                        <p className="text-lg md:text-xl font-semibold text-[#1c5375] tracking-wide">
                                            Learn Your Way™
                                        </p>
                                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1c5375] leading-tight">
                                            Home Education Your Way
                                        </h1>
                                        
                                        <div className="space-y-4 text-[#656565] text-xl md:text-2xl">
                                            <p className="font-semibold">For parents and tutors to create amazing learning experiences</p>
                                            <p className="font-semibold">Help your child learn in the way that works best for them</p>
                                        </div>

                                        <p className="text-[#656565] text-lg md:text-xl leading-relaxed">
                                            You&apos;ll be blown away by our creative AI as it uses your own learning materials, values and beliefs to produce lesson guides, worksheets, and select videos that are uniquely personalised to your child.
                                        </p>

                                        {/* Action Links */}
                                        <div className="flex flex-wrap gap-4 items-center text-lg">
                                            <span className="text-[#656565]">Find out more</span>
                                            <LinkButton 
                                                className="text-[#1c5375] hover:text-[#0f3a52] font-semibold underline decoration-2 underline-offset-4 hover:decoration-[#0f3a52] transition-colors"
                                                onClick={() => location.href = "/features"}>
                                                Our Features
                                            </LinkButton>
                                            <span className="text-[#656565]">|</span>
                                            <LinkButton 
                                                className="text-[#1c5375] hover:text-[#0f3a52] font-semibold underline decoration-2 underline-offset-4 hover:decoration-[#0f3a52] transition-colors"
                                                onClick={() => location.href = "/mission"}>
                                                Our Mission
                                            </LinkButton>
                                            <span className="text-[#656565]">|</span>
                                            <LinkButton 
                                                className="text-[#1c5375] hover:text-[#0f3a52] font-semibold underline decoration-2 underline-offset-4 hover:decoration-[#0f3a52] transition-colors"
                                                onClick={() => location.href = "/home-education"}>
                                                Home Education
                                            </LinkButton>
                                        </div>
                                    </div>

                                    {/* Signup Card */}
                                    <Card className="border-[#1c5375] border-2 shadow-lg">
                                        <CardHeader>
                                            <CardTitle className="text-[#1c5375] text-2xl">
                                                Try it for free, at your pace
                                            </CardTitle>
                                            <CardDescription className="text-[#656565] text-lg">
                                                No payment needed to be amazed by our lessons, plans and assessments.
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="text-lg">
                                                <LinkButton
                                                    className="text-[#1c5375] hover:text-[#0f3a52] font-semibold underline decoration-2 underline-offset-4 hover:decoration-[#0f3a52] transition-colors"
                                                    onClick={() => location.href = "/learning-plans"}>
                                                    View our Learning Packages
                                                </LinkButton>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Right Column - Hero Image */}
                                <div className="flex flex-col items-center lg:items-end">
                                    <img alt="Learn Your Way Mobile App" 
                                         src={mobileHero}
                                         className="w-full max-w-md lg:max-w-lg object-contain drop-shadow-2xl rounded-lg" />
                                </div>
                            </div>

                            {/* Feature Cards */}
                            <div className="grid gap-8 md:grid-cols-3 mb-16">
                                <Card className="border-[#58ac59] border-2 shadow-lg hover:shadow-xl transition-shadow">
                                    <CardHeader>
                                        <CardTitle className="text-[#1c5375] text-xl flex items-center gap-3">
                                            🎯 Personalised Learning
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-[#656565]">Lesson materials created for you that adapt to everything you tell us about your child.</p>
                                        <FeatureScreenshot
                                            src={personalisedLearningScreenshot}
                                            alt="Personalised learning example"
                                            large
                                        />
                                    </CardContent>
                                </Card>

                                <Card className="border-[#7bc8d7] border-2 shadow-lg hover:shadow-xl transition-shadow">
                                    <CardHeader>
                                        <CardTitle className="text-[#1c5375] text-xl flex items-center gap-3">
                                            📋 Simple Planning
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-[#656565]">Freedom within a framework that makes lesson planning effortless and effective.</p>
                                        <FeatureScreenshot
                                            src={simplePlanningScreenshot}
                                            alt="Simple planning example"
                                            rotateClass="rotate-2"
                                        />
                                    </CardContent>
                                </Card>

                                <Card className="border-[#1c5375] border-2 shadow-lg hover:shadow-xl transition-shadow">
                                    <CardHeader>
                                        <CardTitle className="text-[#1c5375] text-xl flex items-center gap-3">
                                            🏠 Home Education Values
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-[#656565]">Technology that respects and enhances the flexibility you want from home education.</p>
                                        <FeatureScreenshot
                                            src={homeEducationValuesScreenshot}
                                            alt="Home education values example"
                                            rotateClass="-rotate-1"
                                            large
                                        />
                                    </CardContent>
                                </Card>
                            </div>

                            <div className="border-2 border-[#1c5375] bg-white rounded-xl p-6 mb-16">
                                <h2 className="text-[#1c5375] text-2xl md:text-3xl font-bold mb-5">What guides us</h2>
                                <div className="grid gap-6 md:grid-cols-3 text-center">
                                    <div className="border-2 border-[#1c5375] rounded-xl p-6">
                                        <div className="text-4xl mb-2">🎓</div>
                                        <h3 className="text-[#1c5375] font-bold text-xl mb-2">Excellence</h3>
                                        <p className="text-[#656565]">Delivering the highest quality educational experiences</p>
                                    </div>
                                    <div className="border-2 border-[#1c5375] rounded-xl p-6">
                                        <div className="text-4xl mb-2">🤗</div>
                                        <h3 className="text-[#1c5375] font-bold text-xl mb-2">Empathy</h3>
                                        <p className="text-[#656565]">Understanding and supporting each family's unique journey</p>
                                    </div>
                                    <div className="border-2 border-[#1c5375] rounded-xl p-6">
                                        <div className="text-4xl mb-2">💡</div>
                                        <h3 className="text-[#1c5375] font-bold text-xl mb-2">Innovation</h3>
                                        <p className="text-[#656565]">Pioneering new ways to enhance learning through technology</p>
                                    </div>
                                </div>
                            </div>

                            <Card className="border-[#58ac59] border-2 shadow-lg hover:shadow-xl transition-shadow">
                                <CardHeader>
                                    <CardTitle className="text-[#1c5375] text-2xl md:text-3xl flex items-center gap-3">
                                        <img
                                            src={logo}
                                            alt="Learn Your Way logo"
                                            className="h-8 w-auto object-contain"
                                        />
                                        Why Learn Your Way?
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-[#656565] text-base md:text-lg leading-relaxed">
                                        When our founders chose to adopt and to home educate, they could not find the help they needed to plan, teach and assess home education. So they pulled together a team that could research the market and build a solution that would leverage the creative power of AI.
                                    </p>
                                    <p className="text-[#656565] text-base md:text-lg leading-relaxed">
                                        Learn Your Way was born, and has evolved to be a flexible and supportive tool to help parents, guardians and tutors harness creative AI in a way that works best for their child or student.
                                    </p>
                                    <p className="text-[#656565] text-base md:text-lg leading-relaxed">
                                        There&apos;s flexibility to adapt learning materials based on age, abilities, and numerous environmental factors, including today&apos;s weather! There&apos;s flexibility to plan as little or a much as you want. And there&apos;s the reassurance that you can assess progress at any time, and generate reports as needed.
                                    </p>
                                    <p className="text-[#656565] text-base md:text-lg leading-relaxed">
                                        Learn Your Way is unique in its exciting use of AI to energise and enhance your child&apos;s education.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </Frame>);
}